import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
  runTransaction,
  arrayUnion,
  Timestamp,
  type DocumentReference,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { uploadFile } from '@/utils/storage';
import { getAuth } from 'firebase/auth';
import { MovimentacaoCreateSchema, MovimentacaoUpdateSchema } from '../models/movimentacao.schemas';
import { calcularEtapaFotos } from '../models/movimentacao.utils';
import type { StatusEtapaProcesso } from '../models/solicitacao.types';
import type {
  Movimentacao,
  MovimentacaoCreate,
  MovimentacaoExclusao,
  MovimentacaoUpdate,
  MovimentacaoItemIntake,
  MovimentacaoItemExecucao,
  StatusMovimentacao,
  TermoAssinado,
} from '../models/movimentacao.types';
import type { AnexoAtivo } from '../models/equipamento.types';

/**
 * Operações Firestore para a subcoleção si_solicitacoes/{id}/movimentacoes.
 * 100% operacional — exclusivo do SI (ver firestore.rules), refletido aqui só como
 * validação de forma, não de autorização.
 */
/** Ver mesma função em solicitacaoService.ts — Firestore rejeita `undefined` em campos. */
function semUndefined<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as T;
}

class MovimentacaoServiceClass {
  private subcollectionName = 'movimentacoes';

  private colRef(solicitacaoId: string) {
    return collection(db, 'si_solicitacoes', solicitacaoId, this.subcollectionName);
  }

  /**
   * `dispararTransicao = false` só pra 1ª Movimentação, criada junto da própria
   * Solicitação (mesmo envio de NovaSolicitacaoContent, ver useSolicitacaoMutations) —
   * ali é só a "abertura" do pedido pelo solicitante, não trabalho do SI. Toda outra
   * criação de Movimentação já é uma ação do SI de verdade (ex. NovaMovimentacaoModal,
   * SI preenchendo tipo/SO/homologado/método na hora de criar), então dispara normalmente
   * (valor padrão `true`) — ver `avancarParaEmAndamentoSeNecessario` abaixo.
   */
  async create(solicitacaoId: string, data: MovimentacaoCreate, dispararTransicao = true): Promise<string> {
    const validated = MovimentacaoCreateSchema.parse(data);

    const docRef = await addDoc(this.colRef(solicitacaoId), semUndefined({
      ...validated,
      statusMovimentacao: 'aguardando_preparacao',
      etapas: {
        fotos: 'pendente',
        termo: 'pendente',
        informativos: [],
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));

    if (dispararTransicao) {
      await this.avancarParaEmAndamentoSeNecessario(solicitacaoId);
    }

    return docRef.id;
  }

  /**
   * `aberta → em_andamento` ("Novo" → "Em Andamento"): automático assim que existe sinal
   * real de trabalho do SI (ou de quem tem permissão de editar a Movimentação) — editar
   * status/etapa/execução/logística de uma Movimentação já existente (`update`), ou criar
   * uma Movimentação com `dispararTransicao = true` (o normal, exceto a 1ª — ver `create`
   * acima). Só age quando o status atual é `aberta`; não toca `em_andamento`, `concluida`
   * nem `cancelada`, então nunca reabre nem reativa uma Solicitação por engano.
   */
  private async avancarParaEmAndamentoSeNecessario(solicitacaoId: string): Promise<void> {
    const solicitacaoRef = doc(db, 'si_solicitacoes', solicitacaoId);
    const snap = await getDoc(solicitacaoRef);
    if (snap.exists() && snap.data().status === 'aberta') {
      await updateDoc(solicitacaoRef, { status: 'em_andamento', updatedAt: serverTimestamp() });
    }
  }

  async update(solicitacaoId: string, data: MovimentacaoUpdate): Promise<void> {
    const validated = MovimentacaoUpdateSchema.parse(data);
    const { id, etapas, ...fields } = validated;
    const docRef = doc(this.colRef(solicitacaoId), id);

    // `updateDoc` substitui o mapa inteiro quando um objeto aninhado é passado direto
    // (ex. `etapas: { fotos: 'concluido' }` apagaria `termo`/`informativos`) — notação
    // de ponto faz o merge parcial de verdade por chave.
    const etapasFields = etapas
      ? Object.fromEntries(Object.entries(etapas).map(([chave, valor]) => [`etapas.${chave}`, valor]))
      : {};

    const statusAutomatico = await this.calcularStatusAutomatico(docRef, fields);
    const etapaFotosAutomatica =
      etapas?.fotos === undefined ? await this.calcularEtapaFotosAutomatica(docRef, fields) : {};

    await updateDoc(docRef, {
      ...fields,
      ...statusAutomatico,
      ...etapaFotosAutomatica,
      ...etapasFields,
      updatedAt: serverTimestamp(),
    });

    await this.avancarParaEmAndamentoSeNecessario(solicitacaoId);
  }

  /**
   * `dataRecebimento` preenchida = o equipamento chegou de verdade no destino final
   * (colaborador, num envio; Raro, numa devolução — `dataRecebimento` é usada nos dois
   * fluxos, ver `MovimentacaoLogisticaSection`) — sinal real de entrega, mesmo espírito
   * de `avancarParaEmAndamentoSeNecessario`. Só age quando quem chamou não está definindo
   * `statusMovimentacao` explicitamente nesta mesma escrita, e nunca sobrescreve
   * `cancelada` nem regride `entregue` (idempotente).
   */
  private async calcularStatusAutomatico(
    docRef: DocumentReference,
    fields: Omit<MovimentacaoUpdate, 'id' | 'etapas'>
  ): Promise<{ statusMovimentacao?: StatusMovimentacao }> {
    if (fields.dataRecebimento == null || fields.statusMovimentacao !== undefined) {
      return {};
    }
    const snap = await getDoc(docRef);
    const statusAtual = snap.data()?.statusMovimentacao as StatusMovimentacao | undefined;
    if (statusAtual && statusAtual !== 'cancelada' && statusAtual !== 'entregue') {
      return { statusMovimentacao: 'entregue' };
    }
    return {};
  }

  /**
   * `etapas.fotos` é derivado (ver calcularEtapaFotos) — adicionar/remover equipamento ou
   * mexer em fotos pode mudar o resultado, então recalcula sobre o documento atual mesclado
   * com o que está sendo escrito. Escritas que não tocam nesses campos não pagam a leitura.
   */
  private async calcularEtapaFotosAutomatica(
    docRef: DocumentReference,
    fields: Omit<MovimentacaoUpdate, 'id' | 'etapas'>
  ): Promise<{ 'etapas.fotos'?: StatusEtapaProcesso }> {
    if (
      fields.equipamentosIntake === undefined &&
      fields.equipamentosExecucao === undefined &&
      fields.fotos === undefined
    ) {
      return {};
    }
    const snap = await getDoc(docRef);
    const atual = (snap.data() ?? {}) as Partial<Movimentacao>;
    return {
      'etapas.fotos': calcularEtapaFotos({
        fotos: fields.fotos !== undefined ? fields.fotos : atual.fotos,
        equipamentosIntake: fields.equipamentosIntake ?? atual.equipamentosIntake ?? [],
        equipamentosExecucao: fields.equipamentosExecucao ?? atual.equipamentosExecucao,
      }),
    };
  }

  /**
   * Fotos de um equipamento específico — em transação (lê o array atual e troca só o item
   * `itemId`), porque o upload leva alguns segundos e o SI pode estar anexando fotos de
   * outro equipamento da mesma Movimentação ao mesmo tempo: montar o array a partir das
   * props (como os demais campos de execução fazem) sobrescreveria as fotos do outro item.
   * Recalcula `etapas.fotos` na mesma escrita.
   */
  async atualizarFotosEquipamento(
    solicitacaoId: string,
    movimentacaoId: string,
    itemId: string,
    alterarFotos: (fotosAtuais: string[]) => string[]
  ): Promise<void> {
    const docRef = doc(this.colRef(solicitacaoId), movimentacaoId);

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      if (!snap.exists()) {
        throw new Error('Movimentação não encontrada.');
      }
      const atual = snap.data() as Movimentacao;
      const execucaoAtual = atual.equipamentosExecucao ?? [];
      const itemAtual = execucaoAtual.find((item) => item.id === itemId);
      const fotos = alterarFotos(itemAtual?.fotos ?? []);

      const equipamentosExecucao = itemAtual
        ? execucaoAtual.map((item) => (item.id === itemId ? { ...item, fotos } : item))
        : [...execucaoAtual, { id: itemId, fotos }];

      transaction.update(docRef, {
        equipamentosExecucao,
        'etapas.fotos': calcularEtapaFotos({ ...atual, equipamentosExecucao }),
        updatedAt: serverTimestamp(),
      });
    });

    await this.avancarParaEmAndamentoSeNecessario(solicitacaoId);
  }

  /**
   * Divide um item de equipamento pra uma Movimentação nova — ex.: monitor + notebook
   * nasceram juntos na abertura da Solicitação, mas normalmente não dá pra mandar/devolver
   * junto (embalagem/transporte diferentes), então o SI separa depois de já ter algum
   * vínculo/execução feito, sem perder esse trabalho. A nova Movimentação nasce com o
   * mesmo tipoFluxo/clienteId da origem (SI completa data/logística depois). Escrita em
   * lote (2 documentos, mesma subcoleção) — não passa pelo `MovimentacaoCreateSchema`
   * (que proíbe `equipamentosExecucao` na criação, regra pensada pra criação por quem
   * abre a Solicitação, não pra essa realocação interna feita pelo SI).
   */
  async moverEquipamentoParaNovaMovimentacao(
    solicitacaoId: string,
    movimentacaoOrigemId: string,
    itemId: string
  ): Promise<string> {
    const origemRef = doc(this.colRef(solicitacaoId), movimentacaoOrigemId);
    const origemSnap = await getDoc(origemRef);
    if (!origemSnap.exists()) {
      throw new Error('Movimentação de origem não encontrada.');
    }
    const origem = origemSnap.data() as Movimentacao;

    const intakeItem = (origem.equipamentosIntake ?? []).find((item) => item.id === itemId);
    if (!intakeItem) {
      throw new Error('Item de equipamento não encontrado na Movimentação de origem.');
    }
    const execucaoItem = (origem.equipamentosExecucao ?? []).find((item) => item.id === itemId);

    const novaRef = doc(this.colRef(solicitacaoId));
    const batch = writeBatch(db);

    batch.set(
      novaRef,
      semUndefined({
        tipoFluxo: origem.tipoFluxo,
        clienteId: origem.clienteId ?? null,
        equipamentosIntake: [intakeItem] as MovimentacaoItemIntake[],
        equipamentosExecucao: execucaoItem ? ([execucaoItem] as MovimentacaoItemExecucao[]) : [],
        statusMovimentacao: 'aguardando_preparacao',
        etapas: {
          fotos: calcularEtapaFotos({
            equipamentosIntake: [intakeItem],
            equipamentosExecucao: execucaoItem ? [execucaoItem] : [],
          }),
          termo: 'pendente',
          informativos: [],
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    );

    const intakeRestante = (origem.equipamentosIntake ?? []).filter((item) => item.id !== itemId);
    const execucaoRestante = (origem.equipamentosExecucao ?? []).filter((item) => item.id !== itemId);
    batch.update(origemRef, {
      equipamentosIntake: intakeRestante,
      equipamentosExecucao: execucaoRestante,
      'etapas.fotos': calcularEtapaFotos({
        fotos: origem.fotos,
        equipamentosIntake: intakeRestante,
        equipamentosExecucao: execucaoRestante,
      }),
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    return novaRef.id;
  }

  /**
   * Termo assinado (D4Sign, pelos dois lados) — fecha a Movimentação e vai junto pra ficha de
   * cada equipamento dela como anexo "Termo" com movimentacaoId, na mesma gravação. Anexar de
   * novo substitui o da Movimentação; o anterior fica na ficha (o SI remove se foi engano) e
   * "Meus equipamentos" usa só o mais recente desta Movimentação.
   */
  async anexarTermoAssinado(
    solicitacaoId: string,
    movimentacao: Pick<Movimentacao, 'id' | 'equipamentosExecucao'>,
    arquivo: File,
    autor: { id: string; name: string },
    dono: { id: string; name: string } | null
  ): Promise<void> {
    const anexoId = crypto.randomUUID();
    const caminho = `si_solicitacoes/${solicitacaoId}/movimentacoes/${movimentacao.id}/assinado-${anexoId}-${arquivo.name}`;
    const url = await uploadFile(arquivo, caminho);
    const agora = Timestamp.now();
    const termoAssinado: TermoAssinado = { url, caminho, nome: arquivo.name, anexadoEm: agora, anexadoPor: autor };

    const batch = writeBatch(db);
    batch.update(doc(this.colRef(solicitacaoId), movimentacao.id), { termoAssinado, updatedAt: serverTimestamp() });
    const equipamentoIds = Array.from(
      new Set((movimentacao.equipamentosExecucao ?? []).map((item) => item.equipamentoId).filter((id): id is string => Boolean(id)))
    );
    equipamentoIds.forEach((equipamentoId) => {
      const anexo: AnexoAtivo = {
        id: anexoId,
        nome: arquivo.name,
        url,
        caminho,
        categoria: 'termo',
        autor: { ...autor, avatar: null },
        criadoEm: agora,
        solicitacaoId,
        movimentacaoId: movimentacao.id,
        colaboradorId: dono?.id ?? null,
        colaboradorNome: dono?.name ?? null,
      };
      batch.update(doc(db, 'si_equipamentos', equipamentoId), {
        anexos: arrayUnion(anexo),
        updatedBy: anexo.autor,
        // Origem pro histórico do ativo: o anexo veio desta Movimentação.
        ultimaAlteracao: { tipo: 'movimentacao', ator: autor, solicitacaoId, movimentacaoId: movimentacao.id, em: serverTimestamp() },
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }

  async getById(solicitacaoId: string, movimentacaoId: string): Promise<Movimentacao | null> {
    const snap = await getDoc(doc(this.colRef(solicitacaoId), movimentacaoId));
    if (!snap.exists()) {
      return null;
    }
    return { id: snap.id, ...snap.data() } as Movimentacao;
  }

  /**
   * Exclusão suave — ver Movimentacao.excluida. Grava direto (sem passar por `update`),
   * porque o schema de update não aceita esses campos de propósito: exclusão é uma ação
   * própria, com motivo obrigatório. `updatedBy` = quem excluiu, pra timeline
   * (onMovimentacaoWriteTimeline registra a mudança de status pra 'cancelada' com esse ator).
   */
  async excluir(solicitacaoId: string, data: MovimentacaoExclusao): Promise<void> {
    if (data.motivoExclusaoTipo === 'outro' && !data.motivoExclusao?.trim()) {
      throw new Error('Descreva o motivo da exclusão.');
    }
    const docRef = doc(this.colRef(solicitacaoId), data.id);
    const atual = await getDoc(docRef);
    if (!atual.exists()) throw new Error('Movimentação não encontrada.');
    // Entregue já mexeu na custódia do Equipamento (Cloud Function de sync) — excluir aqui
    // deixaria o equipamento "com o colaborador" sem Movimentação visível que explique.
    if (atual.data().statusMovimentacao === 'entregue') {
      throw new Error('Movimentação já entregue não pode ser excluída.');
    }

    await updateDoc(docRef, {
      statusMovimentacao: 'cancelada',
      excluida: true,
      motivoExclusaoTipo: data.motivoExclusaoTipo,
      motivoExclusao: data.motivoExclusao?.trim() || null,
      excludedBy: data.excludedBy,
      excludedAt: serverTimestamp(),
      updatedBy: data.excludedBy,
      updatedAt: serverTimestamp(),
    });
  }

  /** Inclui as excluídas (ver Movimentacao.excluida) — quem filtra são os hooks
   * (useMovimentacoesQuery), pra que ativas e excluídas dividam o mesmo cache. */
  async getBySolicitacao(solicitacaoId: string): Promise<Movimentacao[]> {
    const paraLista = (docs: Array<{ id: string; data: () => unknown }>) =>
      docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as Movimentacao);
    try {
      const snap = await getDocs(query(this.colRef(solicitacaoId), orderBy('createdAt', 'asc')));
      return paraLista(snap.docs);
    } catch (erro: any) {
      // Responsável de uma alocação só vê as Movimentações dela (firestore.rules) — lista
      // filtrando pelo próprio e-mail, que é o que a regra consegue garantir.
      const email = getAuth().currentUser?.email?.toLowerCase();
      if (erro?.code !== 'permission-denied' || !email) throw erro;
      const snap = await getDocs(query(this.colRef(solicitacaoId), where('alocacaoResponsavelEmail', '==', email)));
      return paraLista(snap.docs).sort((a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));
    }
  }
}

export const movimentacaoService = new MovimentacaoServiceClass();
